// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning documentation files into headed sections.
 *
 * The HTML path is deliberately NOT Readability. Readability exists to find the
 * prose in a news article and it discards the heading `id` attributes — which are
 * precisely what a docs search needs, because a result should deep-link to
 * `page.html#the-exact-section`, not dump the reader at the top of a 4,000-word
 * page. A structural extractor that strips site chrome and keeps the heading tree
 * is both smaller and strictly better for this job.
 */

export interface Section {
  /** Heading trail from the page title down to this section. */
  heading: string[];
  /** Nearest anchor id, or '' when the section has none. */
  anchor: string;
  text: string;
}

export interface ExtractedDoc {
  path: string;
  title: string;
  sections: Section[];
}

export interface ExtractOptions {
  /** Fenced code blocks are mostly out-of-vocabulary noise for a text embedder. */
  includeCode: boolean;
}

export const DEFAULT_EXTRACT_OPTIONS: ExtractOptions = { includeCode: false };

export const TEXT_EXTENSIONS = ['.md', '.markdown', '.mdx', '.html', '.htm', '.txt'];

export function extensionOf(path: string): string {
  const i = path.lastIndexOf('.');
  return i < 0 ? '' : path.slice(i).toLowerCase();
}

export function isSupported(path: string): boolean {
  return TEXT_EXTENSIONS.includes(extensionOf(path));
}

/** GitHub-flavoured heading slug, used when an HTML heading has no id. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-');
}

/** Strip a leading YAML (or TOML) front-matter block, returning the body. */
export function stripFrontMatter(source: string): { body: string; title: string } {
  const m = /^﻿?(-{3}|\+{3})\r?\n([\s\S]*?)\r?\n\1\r?\n?/.exec(source);
  if (!m) return { body: source, title: '' };
  const titleMatch = /^\s*title\s*[:=]\s*["']?(.+?)["']?\s*$/m.exec(m[2]);
  return { body: source.slice(m[0].length), title: titleMatch ? titleMatch[1].trim() : '' };
}

/** Reduce inline markdown to the words a reader would actually see. */
export function stripInlineMarkdown(line: string): string {
  return line
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
    .replace(/<[^>\n]{1,200}>/g, ' ')
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(?!\s)(.*?)(?<!\s)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^\s{0,3}>+\s?/, '')
    .replace(/^\s{0,3}(?:[-*+]|\d{1,3}[.)])\s+/, '')
    .replace(/^\s{0,3}\|/, '')
    .replace(/\|/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Markdown/MDX → headed sections. */
export function extractMarkdown(
  path: string,
  source: string,
  options: ExtractOptions = DEFAULT_EXTRACT_OPTIONS,
): ExtractedDoc {
  const { body, title: fmTitle } = stripFrontMatter(source);
  const lines = body.split(/\r?\n/);

  const sections: Section[] = [];
  let trail: string[] = [];
  let buffer: string[] = [];
  let anchor = '';
  let docTitle = fmTitle;
  let fence: string | null = null;

  const flush = (): void => {
    const text = buffer.join(' ').replace(/\s{2,}/g, ' ').trim();
    if (text) sections.push({ heading: [...trail], anchor, text });
    buffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const fenceMatch = /^\s{0,3}(```+|~~~+)/.exec(line);
    if (fenceMatch) {
      if (fence === null) fence = fenceMatch[1][0];
      else if (fenceMatch[1][0] === fence) fence = null;
      continue;
    }
    if (fence !== null) {
      if (options.includeCode) buffer.push(line.trim());
      continue;
    }

    const atx = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    const setext =
      !atx && buffer.length > 0 && /^\s{0,3}(=+|-{2,})\s*$/.test(line) && lines[i - 1]?.trim()
        ? { level: line.trim()[0] === '=' ? 1 : 2, text: lines[i - 1].trim() }
        : null;

    if (atx || setext) {
      let level: number;
      let raw: string;
      if (atx) {
        level = atx[1].length;
        raw = atx[2];
      } else {
        level = setext!.level;
        raw = setext!.text;
        buffer.pop();
      }
      flush();
      const heading = stripInlineMarkdown(raw);
      trail = trail.slice(0, level - 1);
      while (trail.length < level - 1) trail.push('');
      trail.push(heading);
      trail = trail.filter((t) => t !== '');
      anchor = slugify(heading);
      if (!docTitle && level === 1) docTitle = heading;
      continue;
    }

    if (/^\s{0,3}(\|?\s*:?-{3,}:?\s*\|)/.test(line)) continue; // table rule
    if (/^\s{0,3}(\*\s*){3,}$/.test(line) || /^\s{0,3}(-\s*){3,}$/.test(line)) continue; // hr

    const text = stripInlineMarkdown(line);
    if (text) buffer.push(text);
  }
  flush();

  const fallback = path.split('/').pop() ?? path;
  return {
    path,
    title: docTitle || sections[0]?.heading[0] || fallback.replace(/\.[^.]+$/, ''),
    sections,
  };
}

/** Plain text → one section. */
export function extractPlainText(path: string, source: string): ExtractedDoc {
  const text = source.replace(/\s+/g, ' ').trim();
  const fallback = (path.split('/').pop() ?? path).replace(/\.[^.]+$/, '');
  return {
    path,
    title: fallback,
    sections: text ? [{ heading: [], anchor: '', text }] : [],
  };
}

const CHROME_SELECTOR = [
  'script',
  'style',
  'noscript',
  'template',
  'svg',
  'nav',
  'header',
  'footer',
  'aside',
  'form',
  '[role="navigation"]',
  '[role="banner"]',
  '[role="contentinfo"]',
  '[role="search"]',
  '[aria-hidden="true"]',
  '.sidebar',
  '.toc',
  '.table-of-contents',
  '.nav',
  '.navbar',
  '.menu',
  '.breadcrumb',
  '.breadcrumbs',
  '.pagination',
  '.edit-link',
  '.skip-link',
  '.theme-toggle',
].join(',');

const MAIN_SELECTOR = ['main', '[role="main"]', 'article', '.content', '.markdown-body', '.prose'].join(',');

const BLOCK_TAGS = new Set([
  'P',
  'LI',
  'TD',
  'TH',
  'DD',
  'DT',
  'BLOCKQUOTE',
  'FIGCAPTION',
  'SUMMARY',
  'CAPTION',
  'PRE',
]);

/**
 * HTML → headed sections. Requires a DOM, so this runs on the main thread; Web
 * Workers have no `DOMParser`.
 */
export function extractHtml(
  path: string,
  source: string,
  options: ExtractOptions = DEFAULT_EXTRACT_OPTIONS,
): ExtractedDoc {
  const doc = new DOMParser().parseFromString(source, 'text/html');
  doc.querySelectorAll(CHROME_SELECTOR).forEach((el) => el.remove());

  const root = doc.querySelector(MAIN_SELECTOR) ?? doc.body;
  const sections: Section[] = [];
  let trail: string[] = [];
  let anchor = '';
  let buffer: string[] = [];

  const flush = (): void => {
    const text = buffer.join(' ').replace(/\s+/g, ' ').trim();
    if (text) sections.push({ heading: [...trail], anchor, text });
    buffer = [];
  };

  const docTitleEl = doc.querySelector('title');
  let docTitle = docTitleEl?.textContent?.trim() ?? '';

  if (root) {
    const walk = (node: Element): void => {
      for (const el of Array.from(node.children)) {
        const tag = el.tagName.toUpperCase();
        const headingLevel = /^H([1-6])$/.exec(tag);
        if (headingLevel) {
          flush();
          const heading = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
          const level = Number(headingLevel[1]);
          trail = trail.slice(0, level - 1).filter((t) => t !== '');
          trail.push(heading);
          anchor = el.id || el.querySelector('[id]')?.id || slugify(heading);
          if (!docTitle && level === 1) docTitle = heading;
          continue;
        }
        if (tag === 'PRE' && !options.includeCode) continue;
        if (BLOCK_TAGS.has(tag)) {
          const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
          if (text) buffer.push(text);
          continue;
        }
        if (el.children.length > 0) {
          walk(el);
        } else {
          const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
          if (text) buffer.push(text);
        }
      }
    };
    walk(root);
  }
  flush();

  const fallback = (path.split('/').pop() ?? path).replace(/\.[^.]+$/, '');
  return { path, title: docTitle || sections[0]?.heading[0] || fallback, sections };
}

/** Dispatch on file extension. */
export function extractDoc(
  path: string,
  source: string,
  options: ExtractOptions = DEFAULT_EXTRACT_OPTIONS,
): ExtractedDoc {
  const ext = extensionOf(path);
  if (ext === '.html' || ext === '.htm') return extractHtml(path, source, options);
  if (ext === '.txt') return extractPlainText(path, source);
  return extractMarkdown(path, source, options);
}
